"use client";

import { useAuth } from "@/contexts/AuthContext";
import type { WorkspaceIntegration } from "@/contexts/AuthContext/useWorkspaceIntegrations";
import type { WorkspaceMemberDisplay } from "@/contexts/AuthContext/useWorkspaceMembers";
import { useDialog } from "@/contexts/DialogContext";
import { useToast } from "@/contexts/ToastContext";
import { useTranslation } from "@client-app/i18n";
import {
  authService,
  type UpdateWorkspaceConnectionAccessGrant,
  type WorkspaceConnectionAccessGrant,
} from "@common/api/authService";
import { mcpConnectorService } from "@common/api/mcpConnectorService";
import { Tooltip } from "@common/index";
import {
    CaretDown,
    CaretLeft,
    CaretRight,
    Check,
    CircleNotch,
    CirclesFour,
    LinkBreak,
    MagnifyingGlass,
    PencilSimple,
    Question,
    User,
    Users,
} from "@phosphor-icons/react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getManagedConnectionRows,
    getVisibleConnectionRows,
} from "./connectionAccessVisibility";
import type { ConnectionData } from "./integrations/ConnectionCard";
import {
    getBrandIcon,
    getBrandIconFrameClassName,
    getBrandIconImageClassName,
} from "./integrations/IntegrationCard";
import {
    AVAILABLE_INTEGRATION_STATUS,
    getIntegrationConnectionStatusMeta,
    isIntegrationConnectionActive,
    resolveKlaviyoIntegrationAvailability,
} from "./integrations/integrationStatus";
import Modal from "./Modal";
import { formatRoleLabel } from "./roleLabels";

// ─── Permission Checkbox Component ──────────────────────────────────────────
interface PermissionCheckboxProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}

const PermissionCheckbox = ({
  checked,
  disabled,
  onChange,
  ariaLabel,
}: PermissionCheckboxProps) => {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={clsx(
        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-all duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20",
        checked
          ? "border-[#1A162F] bg-[#1A162F] text-white"
          : "border-slate-300 bg-white text-transparent hover:border-[#8A93B2]",
        disabled && "cursor-not-allowed opacity-50"
      )}
    >
      <Check size={12} weight="bold" className="pointer-events-none" />
    </button>
  );
};

const ACCESS_TABLE_SKELETON_ROWS = [0, 1, 2];

const ConnectionAccessTableSkeleton = () => (
  <div className="rounded-lg border border-slate-200 bg-white" aria-busy="true">
    <div className="animate-pulse divide-y divide-slate-100">
      {ACCESS_TABLE_SKELETON_ROWS.map((index) => (
        <div key={index} className="grid grid-cols-[minmax(300px,1fr)_140px_200px_156px] gap-4 items-center px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="h-8 w-8 shrink-0 rounded-full bg-slate-100" />
            <div className="min-w-0 space-y-1.5">
              <div className="h-3 w-24 rounded bg-slate-100" />
              <div className="h-2.5 w-36 rounded bg-slate-50" />
            </div>
          </div>
          <div className="h-3 w-16 rounded bg-slate-50" />
          <div className="flex justify-start">
            <div className="h-8 w-[156px] rounded-lg bg-slate-50 border border-slate-100" />
          </div>
          <div className="flex justify-start">
            <div className="h-5 w-9 rounded-full bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

interface BulkAccessDropdownProps {
  onChange: (level: ConnectionAccessLevel) => void;
  disabled?: boolean;
  t: (key: string) => string;
  accessLabel: (level: ConnectionAccessLevel) => string;
}

const BulkAccessDropdown = ({
  onChange,
  disabled,
  t,
  accessLabel,
}: BulkAccessDropdownProps) => {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={clsx(
            "inline-flex h-7 items-center justify-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors outline-none",
            disabled
              ? "opacity-50 cursor-not-allowed text-slate-400"
              : "text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-200"
          )}
        >
          {t("website.integrationsSettings.applyToAll") || "Apply to all"}
          <CaretDown size={12} weight="bold" className={clsx("transition-transform duration-200", open && "rotate-180")} />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-[9999] w-[190px] animate-in fade-in zoom-in-95 rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
        >
          {ACCESS_LEVELS.map((level) => (
            <DropdownMenu.Item
              key={level}
              onSelect={() => onChange(level)}
              className="flex cursor-pointer select-none items-center rounded-md px-2.5 py-2 text-sm text-slate-600 outline-none transition-colors data-[highlighted]:bg-slate-50 data-[highlighted]:text-slate-900"
            >
              {accessLabel(level)}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

type ConnectionAccessLevel = "no_access" | "read" | "action";

const ACCESS_LEVELS: ConnectionAccessLevel[] = ["no_access", "read", "action"];

// ─── Switch Component ────────────────────────────────────────────────────────
interface SwitchProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}

const Switch = ({ checked, disabled, onChange, ariaLabel }: SwitchProps) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2F7BFF]/20",
        checked
          ? "bg-[#2F7BFF]"
          : disabled
            ? "bg-[#E8EDF5]"
            : "bg-[#E2E8F0] hover:bg-[#D8E0EB]",
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      )}
    >
      <span
        className={clsx(
          "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-[0_1px_2px_rgba(15,23,42,0.22)] transition duration-200 ease-in-out",
          checked ? "translate-x-4" : "translate-x-0"
        )}
      />
    </button>
  );
};

interface ManageAccessStatusProps {
  enabled: boolean;
  enabledLabel: string;
  disabledLabel: string;
}

const ManageAccessStatus = ({
  enabled,
  enabledLabel,
  disabledLabel,
}: ManageAccessStatusProps) => (
  <span
    className={clsx(
      "inline-flex h-7 min-w-[92px] items-center justify-center rounded-full px-2.5 text-xs font-medium leading-none",
      enabled
        ? "bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-500/20"
        : "text-[#8A93B2]"
    )}
  >
    {enabled ? enabledLabel : disabledLabel}
  </span>
);

// ─── Access Level Dropdown Component ─────────────────────────────────────────
interface AccessLevelDropdownProps {
  value: ConnectionAccessLevel;
  onChange: (level: ConnectionAccessLevel) => void;
  disabled?: boolean;
  accessLabel: (level: ConnectionAccessLevel) => string;
}

const AccessLevelDropdown = ({
  value,
  onChange,
  disabled,
  accessLabel,
}: AccessLevelDropdownProps) => {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={clsx(
            "inline-flex h-8 w-[156px] items-center justify-between gap-1 rounded-lg border px-2.5 text-xs font-medium outline-none transition-colors",
            disabled
              ? "cursor-not-allowed border-[#E8EBF3] bg-[#F8F9FC] text-[#8A93B2] opacity-60"
              : clsx(
                  "text-[#1A162F] focus-visible:border-[#C8D0E2] focus-visible:bg-[#F8F9FC] focus-visible:ring-2 focus-visible:ring-[#C8D0E2]/35",
                  open
                    ? "border-[#C8D0E2] bg-[#F8F9FC]"
                    : "border-[#E8EBF3] bg-white hover:border-[#C8D0E2] hover:bg-[#F8F9FC]"
                )
          )}
        >
          <span className="truncate">{accessLabel(value)}</span>
          <CaretDown size={12} weight="bold" className={clsx("text-slate-400 transition-transform duration-200 shrink-0", open && "rotate-180")} />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-[9999] w-[168px] animate-in fade-in zoom-in-95 rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
        >
          {ACCESS_LEVELS.map((level) => (
            <DropdownMenu.Item
              key={level}
              onSelect={() => onChange(level)}
              className={clsx(
                "flex cursor-pointer select-none items-center rounded-md px-2.5 py-1.5 text-xs outline-none transition-colors",
                value === level
                  ? "bg-slate-50 text-slate-900 font-semibold"
                  : "text-slate-600 data-[highlighted]:bg-slate-50/70 data-[highlighted]:text-slate-900"
              )}
            >
              {accessLabel(level)}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

interface ConnectionAccessRow {
  id: string;
  connectionId?: string;
  workspaceIntegrationId?: string;
  slugId?: string;
  authType?: string;
  native?: boolean;
  provider: string;
  integrationType: string;
  name: string;
  accountId: string;
  alias: string;
  status: ConnectionData["status"];
  statusLabel?: string;
  statusMessage?: string;
  isUnavailable?: boolean;
  connectedAt: string;
  connectedBy: string;
  subAccounts?: ConnectionData["subAccounts"];
  connectionOwnerKey: string | null;
  isConnectionOwnedByCurrentUser: boolean;
  currentUserAccess: ConnectionAccessLevel;
  isManagedByCurrentUser: boolean;
  canManageAccess: boolean;
}

interface MemberAccessColumn {
  key: string;
  userId: string;
  name: string;
  email: string;
  role: string;
  avatar?: string | null;
  isCurrentUser: boolean;
  isWorkspaceOwner: boolean;
}

const mapBackendConnectionAccessToUi = (accessLevel?: string | null): ConnectionAccessLevel => {
  const normalized = String(accessLevel || "").trim().toUpperCase();
  if (normalized === "WRITE") return "action";
  if (normalized === "READ") return "read";
  return "no_access";
};

const resolveCurrentUserConnectionAccess = (
  mapping: Record<string, any> | undefined,
  integrationRecord: Record<string, any>
): ConnectionAccessLevel => {
  return mapBackendConnectionAccessToUi(
    firstString(
      mapping?.yourAccess,
      mapping?.accessLevel,
      mapping?.currentUserAccess,
      mapping?.current_user_access,
      integrationRecord.yourAccess,
      integrationRecord.accessLevel,
      integrationRecord.currentUserAccess,
      integrationRecord.current_user_access
    )
  );
};

const mapUiConnectionAccessToBackend = (
  accessLevel: ConnectionAccessLevel
): UpdateWorkspaceConnectionAccessGrant["accessLevel"] => {
  if (accessLevel === "action") return "WRITE";
  if (accessLevel === "read") return "READ";
  return "DISABLED";
};

const findMemberForAccessGrant = (
  grant: WorkspaceConnectionAccessGrant,
  members: MemberAccessColumn[]
) => {
  const grantUserId = normalizeText(grant.userId);
  const grantEmail = normalizeText(grant.email);
  return members.find((member) => (
    normalizeText(member.key) === grantUserId ||
    (!!grantEmail && normalizeText(member.email) === grantEmail)
  ));
};

const buildAccessMatrixFromBackendGrants = (
  grants: WorkspaceConnectionAccessGrant[],
  members: MemberAccessColumn[]
) => {
  const accessByMember = members.reduce<Record<string, ConnectionAccessLevel>>((next, member) => {
    next[member.key] = "no_access";
    return next;
  }, {});

  grants.forEach((grant) => {
    const member = findMemberForAccessGrant(grant, members);
    if (!member) return;
    accessByMember[member.key] = mapBackendConnectionAccessToUi(grant.accessLevel);
  });

  return accessByMember;
};

const buildManageAccessMatrixFromBackendGrants = (
  grants: WorkspaceConnectionAccessGrant[],
  members: MemberAccessColumn[]
) => {
  const manageAccessByMember = members.reduce<Record<string, boolean>>((next, member) => {
    next[member.key] = false;
    return next;
  }, {});

  grants.forEach((grant) => {
    const member = findMemberForAccessGrant(grant, members);
    if (!member) return;
    manageAccessByMember[member.key] = Boolean(grant.canManageAccess);
  });

  return manageAccessByMember;
};

const buildConnectionAccessUpdateGrants = (
  members: MemberAccessColumn[],
  draftAccess: Record<string, ConnectionAccessLevel>,
  savedAccess: Record<string, ConnectionAccessLevel>,
  draftManageAccess: Record<string, boolean>,
  savedManageAccess: Record<string, boolean>
): UpdateWorkspaceConnectionAccessGrant[] => (
  members
    .filter((member) => member.userId)
    .map((member) => ({
      userId: member.userId,
      accessLevel: mapUiConnectionAccessToBackend(
        draftAccess[member.key] ?? savedAccess[member.key] ?? "no_access"
      ),
      canManageAccess: draftManageAccess[member.key] ?? savedManageAccess[member.key] ?? false,
    }))
);

interface LoadConnectionAccessGrantsOptions {
  silent?: boolean;
}

const canCurrentMemberManageConnectionAccess = (
  row: ConnectionAccessRow,
  currentMemberKey: string | null,
  manageAccessByMember?: Record<string, boolean>
) => {
  if (!currentMemberKey) {
    return false;
  }

  if (row.canManageAccess) {
    return true;
  }

  if (!manageAccessByMember) {
    return false;
  }

  return Boolean(manageAccessByMember[currentMemberKey]);
};

const shouldPrefetchConnectionAccess = (row: ConnectionAccessRow) => (
  isIntegrationConnectionActive(row) && Boolean(getConnectionAccessBackendId(row))
);

const PROVIDER_LABELS: Record<string, string> = {
  shopify: "Shopify",
  woocommerce: "WooCommerce",
  bigcommerce: "BigCommerce",
  magento: "Magento",
  meta: "Meta Ads",
  "meta-ads": "Meta Ads",
  google: "Google Ads",
  "google-ads": "Google Ads",
  "google-analytics": "Google Analytics",
  "google-search-console": "Google Search Console",
  klaviyo: "Klaviyo",
  "klaviyo-oauth": "Klaviyo",
  mailchimp: "Mailchimp",
  "mailchimp-oauth": "Mailchimp",
  feishu: "Feishu",
  semrush: "Semrush",
  ahrefs: "Ahrefs",
  omnisend: "Omnisend",
  okendo: "Okendo",
  "insider-one": "Insider One",
  "insider-one-oauth": "Insider One",
  emarsys: "Emarsys",
  "emarsys-oauth": "Emarsys",
  judgeme: "Judge.me",
  "facebook-page": "Facebook Page",
  instagram: "Instagram",
  "pinterest-ads": "Pinterest Ads",
  "x-ads": "X Ads",
  wix: "Wix",
  "wix-oauth": "Wix",
};

const PROVIDER_ALIASES: Record<string, string> = {
  "adobe-commerce": "magento",
  googleads: "google-ads",
  klaviyo: "klaviyo-oauth",
  "judgeme-oauth": "judgeme",
  "judge-me": "judgeme",
  "judge.me": "judgeme",
  judge: "judgeme",
  "insider-one-oauth": "insider-one",
  insider: "insider-one",
  useinsider: "insider-one",
  "emarsys-oauth": "emarsys",
  "sap-emarsys": "emarsys",
  "sap-engagement-cloud": "emarsys",
  metaads: "meta-ads",
  facebook: "facebook-page",
  pinterestads: "pinterest-ads",
  pinterest: "pinterest-ads",
  xads: "x-ads",
  "twitter-ads": "x-ads",
  twitterads: "x-ads",
  "twitter-x": "x-ads",
  twitterx: "x-ads",
  twitter: "x-ads",
  x: "x-ads",
};

const normalizeText = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const normalizeProvider = (value: unknown) => {
  const normalized = normalizeText(value).replace(/[\s_]+/g, "-");
  return PROVIDER_ALIASES[normalized] || normalized;
};

const CUSTOM_MCP_PROVIDER_KEY = "custom-mcp";

const isCustomMcpProvider = (provider: string) => (
  normalizeProvider(provider) === CUSTOM_MCP_PROVIDER_KEY
);

const normalizeConnectionIdentity = (value: unknown) =>
  normalizeText(value)
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");

const firstString = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const getIntegrationMappings = (integration: WorkspaceIntegration) => {
  const mappings = Array.isArray(integration.mappings) ? integration.mappings : [];
  return mappings.length > 0 ? mappings : [undefined];
};

const getProviderLabel = (provider: string) => {
  const key = normalizeProvider(provider);
  return PROVIDER_LABELS[key] ?? firstString(provider, key) ?? "Connection";
};

const getConnectionPrimaryColumnLabel = (
  t: (key: string) => string,
  provider?: string | null
) => (
  normalizeProvider(provider) === "shopify"
    ? t("website.integrationsSettings.storeUrl")
    : t("website.integrationsSettings.account")
);

const getConnectionGroupKey = (provider: string) => normalizeProvider(provider);
const getConnectionGroupElementId = (provider: string) => `connection-group-${getConnectionGroupKey(provider)}`;
const getConnectionRowElementKey = (row: Pick<ConnectionAccessRow, "id">) =>
  `connection-row-${normalizeConnectionIdentity(row.id).replace(/[^a-z0-9-]/g, "-") || "item"}`;

const getConnectionRowIdentityKeys = (
  row: Pick<ConnectionAccessRow, "id" | "connectionId" | "workspaceIntegrationId" | "slugId" | "accountId" | "name">
) => {
  const keys = new Set<string>();
  [row.id, row.connectionId, row.workspaceIntegrationId, row.slugId, row.accountId, row.name].forEach((value) => {
    const normalizedValue = normalizeConnectionIdentity(value);
    if (normalizedValue) {
      keys.add(normalizedValue);
    }
  });
  return keys;
};

const getConnectionAccessBackendId = (row: Pick<ConnectionAccessRow, "id" | "workspaceIntegrationId">) => (
  row.workspaceIntegrationId || (Number.isFinite(Number(row.id)) ? row.id : "")
);

const isScrollableAncestor = (element: HTMLElement) => {
  const { overflowY } = window.getComputedStyle(element);
  return /(auto|scroll|overlay)/.test(overflowY) && element.scrollHeight > element.clientHeight;
};

const findClosestScrollContainer = (element: HTMLElement | null) => {
  let current = element?.parentElement ?? null;

  while (current) {
    if (isScrollableAncestor(current)) {
      return current;
    }
    current = current.parentElement;
  }

  return null;
};

const scrollConnectionGroupIntoView = (element: HTMLElement) => {
  const scrollContainer = findClosestScrollContainer(element);

  if (!scrollContainer) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const topOffset = 24;
  const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
  const nextScrollTop = Math.min(
    Math.max(0, scrollContainer.scrollTop + (elementRect.top - containerRect.top) - topOffset),
    maxScrollTop
  );

  scrollContainer.scrollTo({
    top: nextScrollTop,
    behavior: "smooth",
  });
};

const normalizeConnectionStatusValue = (
  status: unknown,
  fallback: ConnectionData["status"] = "disconnected"
): ConnectionData["status"] => {
  const normalized = normalizeText(status);

  if (!normalized) {
    return fallback;
  }

  if (
    normalized === "1" ||
    normalized === "true" ||
    normalized === "active" ||
    normalized === "connected"
  ) {
    return "connected";
  }

  if (
    normalized === "0" ||
    normalized === "false" ||
    normalized === "inactive" ||
    normalized === "disconnected"
  ) {
    return "disconnected";
  }

  if (normalized === "error" || normalized === "failed") {
    return "error";
  }

  if (
    normalized === "disabled" ||
    normalized === "suspended" ||
    normalized === "trial_expired"
  ) {
    return normalized as ConnectionData["status"];
  }

  return fallback;
};

const getMemberKey = (member: WorkspaceMemberDisplay) =>
  firstString(
    member.userId,
    member.id,
    member.email,
    member.displayName,
    member.name
  );

const getMemberRole = (member: WorkspaceMemberDisplay) =>
  firstString(
    member.dataRoleName,
    member.workspaceRole,
    member.membershipRole,
    member.role
  );

const isWorkspaceOwnerRole = (role: unknown) => {
  const normalizedRole = normalizeText(role).replace(/[\s_-]+/g, "");
  return normalizedRole === "owner" || normalizedRole.includes("owner");
};

const getMemberDisplayRole = (member: MemberAccessColumn, t: (key: string) => string) => {
  if (!member.role) return "";
  return formatRoleLabel(String(member.role), t, { titleCaseFallback: true });
};

const normalizeConnectionUserMessage = (
  message?: string | null,
  fallback = "Connection failed. Please try again."
) => {
  if (!message) {
    return fallback;
  }

  return message
    .replace(/Zernio/gi, "Bustly")
    .replace(/Zerinio/gi, "Bustly")
    .replace(/zerinio/gi, "Bustly")
    .replace(/zernio/gi, "Bustly");
};

const resolveConnectionOwnerKey = (
  connectedBy: string,
  members: MemberAccessColumn[]
) => {
  const connectedByText = normalizeText(connectedBy);
  if (!connectedByText) return null;
  const exactMatch = members.find((member) =>
    [member.name, member.email, member.key].some((value) => normalizeText(value) === connectedByText)
  );
  if (exactMatch) return exactMatch.key;
  const fuzzyMatch = members.find((member) =>
    [member.name, member.email].some((value) => {
      const normalizedValue = normalizeText(value);
      return normalizedValue && connectedByText.includes(normalizedValue);
    })
  );
  return fuzzyMatch?.key ?? null;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const getReadableDisplayValue = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue || UUID_PATTERN.test(trimmedValue)) {
    return null;
  }

  return trimmedValue;
};

const getNonEmptyDisplayValue = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }

  const text = String(value).trim();
  return text || null;
};

const FEISHU_INTERNAL_ACCOUNT_PATTERN = /^feishu:[^:]+:.+$/i;
const WORKSPACE_INTEGRATION_SLUG_PATTERN = /^wi_[0-9a-z]{10}$/i;

const getFeishuReadableDisplayValue = (value: unknown): string | null => {
  const text = getNonEmptyDisplayValue(value);
  if (!text || FEISHU_INTERNAL_ACCOUNT_PATTERN.test(text) || WORKSPACE_INTEGRATION_SLUG_PATTERN.test(text)) {
    return null;
  }

  return text;
};

const resolveConnectionUserDisplayName = (
  mapping?: Record<string, any>,
  profile: Record<string, any> = {}
) => {
  const profileUser = profile.user || profile.connected_by || profile.created_by || {};
  const displayName = [
    mapping?.connected_by_name,
    mapping?.user_name,
    mapping?.created_by_name,
    mapping?.connectedByName,
    mapping?.userName,
    mapping?.createdByName,
    profile.connected_by_name,
    profile.user_name,
    profile.created_by_name,
    profile.name,
    profile.full_name,
    profileUser.name,
    profileUser.full_name,
    mapping?.authorized_account_email,
    mapping?.authorizedAccountEmail,
    mapping?.external_account_email,
    mapping?.externalAccountEmail,
    mapping?.connected_by_email,
    mapping?.user_email,
    mapping?.created_by_email,
    mapping?.email,
    profile.authorized_account_email,
    profile.authorizedAccountEmail,
    profile.external_account_email,
    profile.externalAccountEmail,
    profile.connected_by_email,
    profile.user_email,
    profile.created_by_email,
    profile.email,
    profileUser.authorized_account_email,
    profileUser.authorizedAccountEmail,
    profileUser.external_account_email,
    profileUser.externalAccountEmail,
    profileUser.email,
    mapping?.connected_by,
    mapping?.created_by,
    mapping?.user_id,
  ].map(getReadableDisplayValue).find(Boolean);

  return displayName || "Admin";
};

const resolveFeishuConnectionDisplayName = (
  profile: Record<string, any> = {},
  integration: Record<string, any> = {},
  mapping?: Record<string, any>
) => {
  const integrationProfile = integration.profile_data || {};
  return [
    mapping?.external_account_name,
    mapping?.externalAccountName,
    integration.external_account_name,
    integration.externalAccountName,
    mapping?.authorized_account_name,
    mapping?.authorizedAccountName,
    profile.authorized_account_name,
    profile.authorizedAccountName,
    profile.external_account_name,
    profile.externalAccountName,
    profile.accountName,
    profile.name,
    integrationProfile.authorized_account_name,
    integrationProfile.authorizedAccountName,
    integrationProfile.external_account_name,
    integrationProfile.externalAccountName,
    integrationProfile.accountName,
    integrationProfile.name,
    mapping?.account_name,
    mapping?.name,
    mapping?.label,
    integration.label,
  ].map(getFeishuReadableDisplayValue).find(Boolean) || "";
};

const resolveGoogleAdsAuthorizedAccountValue = (
  profile: Record<string, any> = {},
  integration: Record<string, any> = {},
  mapping?: Record<string, any>
) => {
  const integrationProfile = integration.profile_data || {};
  const workspaceExternalAccountName =
    getNonEmptyDisplayValue(mapping?.external_account_name) ||
    getNonEmptyDisplayValue(mapping?.externalAccountName) ||
    getNonEmptyDisplayValue(integration.external_account_name) ||
    getNonEmptyDisplayValue(integration.externalAccountName);

  if (workspaceExternalAccountName) {
    return workspaceExternalAccountName;
  }

  return [
    mapping?.authorized_account_email,
    mapping?.authorizedAccountEmail,
    mapping?.external_account_email,
    mapping?.externalAccountEmail,
    mapping?.authorized_account_name,
    mapping?.authorizedAccountName,
    mapping?.external_account_name,
    mapping?.externalAccountName,
    profile.authorized_account_email,
    profile.authorizedAccountEmail,
    profile.external_account_email,
    profile.externalAccountEmail,
    profile.authorized_account_name,
    profile.authorizedAccountName,
    profile.external_account_name,
    profile.externalAccountName,
    profile.email,
    profile.accountEmail,
    profile.loginEmail,
    profile.accountName,
    integrationProfile.authorized_account_email,
    integrationProfile.authorizedAccountEmail,
    integrationProfile.external_account_email,
    integrationProfile.externalAccountEmail,
    integrationProfile.authorized_account_name,
    integrationProfile.authorizedAccountName,
    integrationProfile.external_account_name,
    integrationProfile.externalAccountName,
    integrationProfile.email,
    integrationProfile.accountEmail,
    integrationProfile.loginEmail,
    integrationProfile.accountName,
    integration.external_account_name,
    integration.externalAccountName,
    mapping?.authorized_account_id,
    mapping?.authorizedAccountId,
    mapping?.external_account_id,
    mapping?.externalAccountId,
    integration.external_account_id,
    integration.externalAccountId,
    mapping?.connection_id,
  ].map(getNonEmptyDisplayValue).find(Boolean) || "";
};

const resolveConnectionOwnerKeyFromMapping = (
  mapping: Record<string, any> | undefined,
  connectedBy: string,
  members: MemberAccessColumn[]
) => {
  const explicitOwner = [
    mapping?.user_id,
    mapping?.connected_by_user_id,
    mapping?.created_by_user_id,
    mapping?.connectedByUserId,
    mapping?.createdByUserId,
    mapping?.connected_by_email,
    mapping?.created_by_email,
    mapping?.connectedByEmail,
    mapping?.createdByEmail,
  ].map(getNonEmptyDisplayValue).find(Boolean);

  if (explicitOwner) {
    const explicitMatch = members.find((member) =>
      [member.key, member.email].some((value) => normalizeText(value) === normalizeText(explicitOwner))
    );

    if (explicitMatch) {
      return explicitMatch.key;
    }
  }

  return resolveConnectionOwnerKey(connectedBy, members);
};

const buildConnectionRows = (
  allIntegrations: WorkspaceIntegration[],
  members: MemberAccessColumn[],
  currentMemberKey: string | null
) => {
  return allIntegrations.flatMap((integration) => {
    const provider = getProviderLabel(integration.provider);
    if (!integration.mappings?.length && !integration.connected) {
      return [];
    }

    return getIntegrationMappings(integration).map((rawMapping, index) => {
      const mapping = rawMapping as Record<string, any> | undefined;
      const profileData = (integration.profile_data ?? {}) as Record<string, any>;
      const integrationRecord = integration as unknown as Record<string, any>;
      const id = String(mapping?.id ?? `${normalizeProvider(integration.provider)}-${index}`);
      const workspaceConnectionStatus: ConnectionData["status"] = integration.connected ? "connected" : "disconnected";
      const normalizedConnectionStatus = normalizeConnectionStatusValue(mapping?.status, workspaceConnectionStatus);
      const availability = provider === "Klaviyo"
        ? resolveKlaviyoIntegrationAvailability(integration)
        : AVAILABLE_INTEGRATION_STATUS;
      const displayStatus = availability.isUnavailable
        ? availability.status
        : integration.connected && normalizedConnectionStatus === "disconnected"
          ? "connected"
          : normalizedConnectionStatus;
      const isShopifyConnection = provider === "Shopify";
      const isGoogleAdsConnection = provider === "Google Ads";
      const isBigCommerceConnection = provider === "BigCommerce";
      const isFeishuConnection = provider === "Feishu";
      const googleAdsAuthorizedAccountValue = isGoogleAdsConnection
        ? resolveGoogleAdsAuthorizedAccountValue(profileData, integrationRecord, mapping)
        : "";
      const feishuAccountDisplayValue = isFeishuConnection
        ? resolveFeishuConnectionDisplayName(profileData, integrationRecord, mapping)
        : "";
      const accountValue = isShopifyConnection
        ? profileData.shop_domain || profileData.shopDomain || mapping?.account_name || mapping?.connection_id || ""
        : isGoogleAdsConnection
          ? googleAdsAuthorizedAccountValue
          : isFeishuConnection
            ? feishuAccountDisplayValue
            : isBigCommerceConnection
              ? mapping?.name || mapping?.account_name || mapping?.account_id || profileData.accountName || profileData.name || mapping?.connection_id || ""
              : profileData.accountId || mapping?.account_name || mapping?.account_id || profileData.username || mapping?.connection_id || "";
      const connectionLabel = mapping?.label || "";
      const normalizedConnectionId = mapping?.connection_id || mapping?.nango_connection_id || undefined;
      const connectedBy = resolveConnectionUserDisplayName(mapping, profileData);
      const ownerKey = resolveConnectionOwnerKeyFromMapping(mapping, connectedBy, members);
      const isConnectionOwnedByCurrentUser = !!currentMemberKey && ownerKey === currentMemberKey;
      const canManageAccess = Boolean(integrationRecord.canManageAccess ?? mapping?.canManageAccess);
      const currentUserAccess = resolveCurrentUserConnectionAccess(
        mapping,
        integrationRecord
      );

      return {
        id,
        connectionId: normalizedConnectionId,
        workspaceIntegrationId: mapping?.id == null ? undefined : String(mapping.id),
        slugId: mapping?.slugId || profileData.slugId,
        authType: String(mapping?.authType || integrationRecord.authType || ""),
        native: Boolean(mapping?.native ?? integrationRecord.native),
        provider,
        integrationType: provider,
        name: isGoogleAdsConnection
          ? googleAdsAuthorizedAccountValue
          : isFeishuConnection
            ? feishuAccountDisplayValue
            : profileData.connection_name || profileData.accountName || mapping?.name || mapping?.account_name || `${provider} Workspace`,
        accountId: String(accountValue || "-"),
        alias: String(connectionLabel || ""),
        status: displayStatus,
        statusLabel: availability.label,
        statusMessage: availability.message,
        isUnavailable: availability.isUnavailable,
        connectedAt: firstString(
          mapping?.created_at,
          integration.created_at,
          "Recent"
        ),
        connectedBy,
        subAccounts: isGoogleAdsConnection
          ? (mapping?.sub_accounts || []).map((subAccount: Record<string, unknown>) => ({
              accountName: String(subAccount.account_name || subAccount.customer_id || subAccount.account_id || "Google Ads sub account"),
              customerId: String(subAccount.customer_id || subAccount.account_id || ""),
            })).filter((subAccount: { customerId: string }) => subAccount.customerId)
          : undefined,
        connectionOwnerKey: ownerKey,
        isConnectionOwnedByCurrentUser,
        currentUserAccess,
        canManageAccess,
        isManagedByCurrentUser: canManageAccess,
      } satisfies ConnectionAccessRow;
    });
  });
};

const buildMemberColumns = (members: WorkspaceMemberDisplay[]) =>
  members
    .map<MemberAccessColumn | null>((member) => {
      const key = getMemberKey(member);
        if (!key) return null;
        const role = getMemberRole(member);
        return {
          key,
          userId: firstString(member.userId, member.id),
          name: firstString(member.displayName, member.name, member.email, "Member"),
          email: firstString(member.email),
          role,
        avatar: member.avatar,
        isCurrentUser: Boolean(member.isCurrentUser),
        isWorkspaceOwner:
          isWorkspaceOwnerRole(member.membershipRole) ||
          isWorkspaceOwnerRole(member.workspaceRole) ||
          isWorkspaceOwnerRole(role),
      };
    })
    .filter((member): member is MemberAccessColumn => Boolean(member));

const formatConnectionTimestamp = (locale: string, connectedAt: string) => {
  if (!connectedAt || connectedAt === "Recent") {
    return null;
  }

  const parsedDate = new Date(connectedAt);
  if (Number.isNaN(parsedDate.getTime())) {
    return connectedAt;
  }

  const includesExplicitTime = connectedAt.includes("T") || /\d:\d{2}/.test(connectedAt);
  return new Intl.DateTimeFormat(
    locale === "zh-CN" ? "zh-CN" : "en-US",
    includesExplicitTime
      ? {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: false,
        }
      : {
          month: "short",
          day: "numeric",
          year: "numeric",
        }
  ).format(parsedDate);
};

const formatConnectionMetaLine = (
  t: (key: string, values?: Record<string, string>) => string,
  locale: string,
  connection: Pick<ConnectionAccessRow, "connectedAt" | "connectedBy">
) => {
  const formattedTimestamp = formatConnectionTimestamp(locale, connection.connectedAt);

  if (!formattedTimestamp) {
    return connection.connectedBy
      ? t("website.integrationsSettings.connectedRecentlyBy", { user: connection.connectedBy })
      : t("website.integrationsSettings.connectedRecently");
  }

  return connection.connectedBy
    ? t("website.integrationsSettings.connectedByOn", {
        user: connection.connectedBy,
        date: formattedTimestamp,
      })
    : t("website.integrationsSettings.connectedOn", { date: formattedTimestamp });
};

const getConnectionOpenTimestamp = (row: Pick<ConnectionAccessRow, "connectedAt">) => {
  if (!row.connectedAt || row.connectedAt === "Recent") {
    return Number.MAX_SAFE_INTEGER;
  }

  const parsedTimestamp = new Date(row.connectedAt).getTime();
  return Number.isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
};

const findRequestedAccessRow = (rows: ConnectionAccessRow[], provider: string | null) => {
  const providerId = normalizeProvider(provider);
  if (!providerId) {
    return null;
  }

  return [...rows]
    .filter((row) => (
      getConnectionGroupKey(row.provider) === providerId
      && row.isManagedByCurrentUser
      && isIntegrationConnectionActive(row)
    ))
    .sort((left, right) => getConnectionOpenTimestamp(right) - getConnectionOpenTimestamp(left))[0] ?? null;
};


export type ConnectionAccessFilter = "all" | "owned_by_me";
const CONNECTION_ACCESS_FILTERS: ConnectionAccessFilter[] = ["all", "owned_by_me"];
type ConnectionAccessViewState = "loading" | "empty" | "ready";

const getConnectionAccessFilterLabelKey = (filter: ConnectionAccessFilter) => (
  filter === "owned_by_me"
    ? "website.integrationsSettings.ownedByMe"
    : "website.integrationsSettings.allConnections"
);

interface ConnectionFilterSelectProps {
  value: ConnectionAccessFilter;
  onChange: (value: ConnectionAccessFilter) => void;
  t: (key: string) => string;
}

export const ConnectionFilterSelect = ({
  value,
  onChange,
  t,
}: ConnectionFilterSelectProps) => {
  const [open, setOpen] = useState(false);
  const selectedLabel = t(getConnectionAccessFilterLabelKey(value));

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t("website.integrationsSettings.connectionFilter")}
          className={clsx(
            "inline-flex h-10 w-full items-center justify-between gap-3 rounded-[10px] border bg-white px-4 text-left text-sm font-medium text-[#1A162F] outline-none transition-all sm:w-[280px]",
            open
              ? "border-[#1A162F] ring-2 ring-[#1A162F]/20"
              : "border-[#DDE3EF] hover:border-[#C8D0E2] hover:bg-[#F8F9FC]"
          )}
        >
          <span className="truncate">{selectedLabel}</span>
          <CaretDown
            size={16}
            weight="bold"
            className={clsx(
              "shrink-0 text-[#6B7894] transition-transform duration-200",
              open && "rotate-180"
            )}
          />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[10002] min-w-[280px] overflow-hidden rounded-xl border border-[#E8EBF3] bg-white p-1.5 shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
          sideOffset={6}
          align="start"
          collisionPadding={8}
        >
          {CONNECTION_ACCESS_FILTERS.map((filter) => {
            const isSelected = filter === value;

            return (
              <DropdownMenu.Item
                key={filter}
                onSelect={() => onChange(filter)}
                className={clsx(
                  "flex cursor-pointer items-center justify-between gap-3 rounded-[8px] px-3 py-2 text-sm font-medium outline-none transition-colors",
                  isSelected
                    ? "bg-[#F4F5F8] text-[#1A162F]"
                    : "text-[#44484F] hover:bg-[#F8F9FC]"
                )}
              >
                <span className="truncate">{t(getConnectionAccessFilterLabelKey(filter))}</span>
                {isSelected ? <Check size={14} weight="bold" className="shrink-0 text-[#1A162F]" /> : null}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

const getConnectionSearchText = (row: ConnectionAccessRow) => [
  row.provider,
  row.integrationType,
  row.name,
  row.accountId,
  row.alias,
  row.connectedBy,
  row.status,
  row.statusLabel,
  row.connectedAt,
].map(normalizeText).join(" ");

const filterConnectionAccessRows = (
  rows: ConnectionAccessRow[],
  filter: ConnectionAccessFilter,
  searchQuery: string
) => {
  const normalizedQuery = normalizeText(searchQuery);
  const filterRows = filter === "owned_by_me" ? getManagedConnectionRows(rows) : rows;

  return filterRows.filter((row) => {
    if (!normalizedQuery) {
      return true;
    }

    return getConnectionSearchText(row).includes(normalizedQuery);
  });
};

const getAssignableMembersForRow = (
  members: MemberAccessColumn[],
  row: ConnectionAccessRow
) => {
  return [...members].sort((a, b) => {
    const aIsOwner = a.isWorkspaceOwner || isWorkspaceOwnerRole(a.role) || a.key === row.connectionOwnerKey;
    const bIsOwner = b.isWorkspaceOwner || isWorkspaceOwnerRole(b.role) || b.key === row.connectionOwnerKey;
    if (aIsOwner && !bIsOwner) return -1;
    if (!aIsOwner && bIsOwner) return 1;
    return 0;
  });
};

const getConnectionDisconnectTrackingId = (row: ConnectionAccessRow) =>
  row.provider === "Feishu"
    ? row.slugId || row.connectionId || row.id
    : row.connectionId || row.id;

const getDisconnectPlatformValue = (provider: string) =>
  provider.toLowerCase().replace(/\s+/g, "_");




// ─── Status Text ────────────────────────────────────────────────────────────

const getConnectionStatusTextClassName = (status: ConnectionData["status"]) => {
  if (isIntegrationConnectionActive({ status })) {
    return "text-emerald-700";
  }
  if (status === "error") {
    return "text-red-700";
  }
  if (status === "disabled" || status === "suspended" || status === "trial_expired") {
    return "text-amber-700";
  }
  return "text-slate-500";
};

const ConnectionStatusText = ({
  status,
  statusLabel,
  isHighlighted = false,
}: Pick<ConnectionAccessRow, "status" | "statusLabel"> & { isHighlighted?: boolean }) => {
  const statusMeta = getIntegrationConnectionStatusMeta({ status, statusLabel });

  return (
    <span
      className={clsx(
        "inline-flex max-w-full items-center gap-1.5 text-xs font-medium",
        getConnectionStatusTextClassName(status),
        isHighlighted && "connection-status-highlight"
      )}
    >
      <span className={clsx("h-1.5 w-1.5 shrink-0 rounded-full", statusMeta.dotClassName)} />
      <span className="truncate">{statusMeta.label}</span>
    </span>
  );
};

const useIsTextTruncated = (
  textRef: { current: HTMLElement | null },
  text: string
) => {
  const [isTruncated, setIsTruncated] = useState(false);

  const updateTruncationState = useCallback(() => {
    const element = textRef.current;
    if (!element) {
      setIsTruncated(false);
      return false;
    }

    const nextIsTruncated = element.scrollWidth > element.clientWidth + 1;
    setIsTruncated((previous) => (
      previous === nextIsTruncated ? previous : nextIsTruncated
    ));
    return nextIsTruncated;
  }, [textRef]);

  useEffect(() => {
    const element = textRef.current;
    if (!element) {
      setIsTruncated(false);
      return;
    }

    updateTruncationState();
    const frameId = window.requestAnimationFrame(updateTruncationState);
    void document.fonts?.ready.then(updateTruncationState);

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateTruncationState);
      observer.observe(element);
      if (element.parentElement) {
        observer.observe(element.parentElement);
      }
      return () => {
        window.cancelAnimationFrame(frameId);
        observer.disconnect();
      };
    }

    window.addEventListener("resize", updateTruncationState);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updateTruncationState);
    };
  }, [text, textRef, updateTruncationState]);

  return { isTruncated, updateTruncationState };
};

const TruncatedTooltipText = ({
  text,
  className,
}: {
  text: string;
  className?: string;
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const { isTruncated, updateTruncationState } = useIsTextTruncated(textRef, text);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const handleMouseEnter = () => {
    setIsTooltipOpen(updateTruncationState());
  };
  const handleMouseLeave = () => {
    setIsTooltipOpen(false);
  };
  const textNode = (
    <span
      ref={textRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={clsx(className, isTruncated && "cursor-help")}
    >
      {text}
    </span>
  );

  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <TooltipPrimitive.Root open={isTooltipOpen && isTruncated}>
        <TooltipPrimitive.Trigger asChild>
          {textNode}
        </TooltipPrimitive.Trigger>
        {isTruncated ? (
          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              className="z-[10002] rounded-md bg-[#1A162F] px-2.5 py-1.5 text-xs font-medium leading-none text-white shadow-[0_8px_24px_rgba(26,22,47,0.18)]"
              side="top"
              align="start"
              sideOffset={8}
              collisionPadding={8}
              avoidCollisions
              sticky="partial"
            >
              {text}
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        ) : null}
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};

interface ConnectionAccessSettingsProps {
  isRefreshingConnections?: boolean;
  showHeader?: boolean;
  connectionFilter?: ConnectionAccessFilter;
  searchQuery?: string;
  providerFilter?: string | null;
  highlightedConnectionKey?: string | null;
  highlightedConnectionProvider?: string | null;
  onHighlightComplete?: () => void;
  requestedAccessProvider?: string | null;
  onAccessRequestComplete?: () => void;
  onConnectionDisconnected?: () => void | Promise<void>;
}

// ─── Main Component ─────────────────────────────────────────────────────────

export const MEMBERS_PER_PAGE = 5;
const MAX_VISIBLE_PAGE_BUTTONS = 5;

export const ConnectionAccessSettings = ({
  isRefreshingConnections = false,
  showHeader = true,
  connectionFilter = "all",
  searchQuery = "",
  providerFilter = null,
  highlightedConnectionKey = null,
  highlightedConnectionProvider = null,
  onHighlightComplete,
  requestedAccessProvider = null,
  onAccessRequestComplete,
  onConnectionDisconnected,
}: ConnectionAccessSettingsProps) => {
  const { t, locale } = useTranslation();
  const toast = useToast();
  const { showDialog } = useDialog();
  const { currentWorkspace, integrations, workspaceMembers } = useAuth();
  const [connectionAccessById, setConnectionAccessById] = useState<Record<string, Record<string, ConnectionAccessLevel>>>({});
  const [connectionManageAccessById, setConnectionManageAccessById] = useState<Record<string, Record<string, boolean>>>({});
  const [loadingAccessConnectionIds, setLoadingAccessConnectionIds] = useState<Set<string>>(new Set());
  const [savingAccessConnectionIds, setSavingAccessConnectionIds] = useState<Set<string>>(new Set());
  const [connectionAccessErrors, setConnectionAccessErrors] = useState<Record<string, string>>({});
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const lastHighlightRequestRef = useRef<string | null>(null);

  const [modalAccessDraft, setModalAccessDraft] = useState<Record<string, ConnectionAccessLevel>>({});
  const [modalManageAccessDraft, setModalManageAccessDraft] = useState<Record<string, boolean>>({});
  const [activeAccessConnectionId, setActiveAccessConnectionId] = useState<string | null>(null);
  const [connectionAliasOverrides, setConnectionAliasOverrides] = useState<Record<string, string>>({});
  const [editingLabelConnection, setEditingLabelConnection] = useState<ConnectionAccessRow | null>(null);
  const [editingLabelValue, setEditingLabelValue] = useState("");
  const [isSavingConnectionLabel, setIsSavingConnectionLabel] = useState(false);
  const [membersPage, setMembersPage] = useState(1);
  const [disconnectingConnectionIds, setDisconnectingConnectionIds] = useState<Set<string>>(new Set());
  const [viewingDetailsConnection, setViewingDetailsConnection] = useState<ConnectionAccessRow | null>(null);
  const disconnectOperationKeysRef = useRef<Set<string>>(new Set());

  const members = useMemo(
    () => buildMemberColumns(workspaceMembers.members ?? []),
    [workspaceMembers.members]
  );
  const currentMemberKey = useMemo(
    () => members.find((member) => member.isCurrentUser)?.key ?? null,
    [members]
  );
  const isConnectionAccessLoading = integrations.isLoading || workspaceMembers.isLoading || isRefreshingConnections;
  const canManageConnectionAccessForRow = useCallback((row: ConnectionAccessRow) => (
    canCurrentMemberManageConnectionAccess(
      row,
      currentMemberKey,
      connectionManageAccessById[row.id]
    )
  ), [connectionManageAccessById, currentMemberKey]);

  const rows = useMemo(
    () =>
      buildConnectionRows(
        integrations.allIntegrations ?? [],
        members,
        currentMemberKey
      ).map((row) => {
        const overriddenAlias = connectionAliasOverrides[row.id];
        return typeof overriddenAlias === "undefined"
          ? row
          : {
              ...row,
              alias: overriddenAlias,
            };
      }),
    [
      connectionAliasOverrides,
      currentMemberKey,
      integrations.allIntegrations,
      members,
    ]
  );

  const rowsWithResolvedManageAccess = useMemo(
    () => rows.map((row) => {
      const resolvedCurrentUserAccess = currentMemberKey
        ? connectionAccessById[row.id]?.[currentMemberKey] ?? row.currentUserAccess
        : row.currentUserAccess;
      return {
        ...row,
        currentUserAccess: resolvedCurrentUserAccess,
        isManagedByCurrentUser: canManageConnectionAccessForRow(row),
      };
    }),
    [canManageConnectionAccessForRow, connectionAccessById, currentMemberKey, rows]
  );

  const workspaceVisibleRows = useMemo(
    () => getVisibleConnectionRows(rowsWithResolvedManageAccess),
    [rowsWithResolvedManageAccess]
  );

  const visibleRows = useMemo(
    () => filterConnectionAccessRows(workspaceVisibleRows, connectionFilter, searchQuery),
    [connectionFilter, searchQuery, workspaceVisibleRows]
  );

  const scopedVisibleRows = useMemo(() => {
    if (!providerFilter) {
      return visibleRows;
    }

    const providerKey = normalizeProvider(providerFilter);
    return visibleRows.filter((row) => normalizeProvider(row.provider) === providerKey);
  }, [providerFilter, visibleRows]);
  const hasConnectionRows = scopedVisibleRows.length > 0;
  const isProviderScopedView = Boolean(providerFilter);
  const showProviderIcon = !isProviderScopedView;
  const primaryColumnLabel = getConnectionPrimaryColumnLabel(t, providerFilter);
  const showActionColumn = scopedVisibleRows.some((row) => (
    row.isManagedByCurrentUser && isIntegrationConnectionActive(row)
  ));

  const providerAnchorRowIds = useMemo(() => {
    const anchorRowIds = new Map<string, string>();

    scopedVisibleRows.forEach((row) => {
      const providerKey = getConnectionGroupKey(row.provider);
      if (!anchorRowIds.has(providerKey)) {
        anchorRowIds.set(providerKey, row.id);
      }
    });

    return anchorRowIds;
  }, [scopedVisibleRows]);

  const activeAccessRow = useMemo(
    () => scopedVisibleRows.find((row) => row.id === activeAccessConnectionId) ?? null,
    [activeAccessConnectionId, scopedVisibleRows]
  );

  const activeAssignableMembers = useMemo(
    () => activeAccessRow ? getAssignableMembersForRow(members, activeAccessRow) : [],
    [activeAccessRow, members]
  );

  const activeAssignableMemberKeys = useMemo(
    () => activeAssignableMembers.map((member) => member.key).join("\u0000"),
    [activeAssignableMembers]
  );

  const isActiveAccessLoading = activeAccessRow
    ? loadingAccessConnectionIds.has(activeAccessRow.id)
    : false;
  const isSavingActiveAccess = activeAccessRow
    ? savingAccessConnectionIds.has(activeAccessRow.id)
    : false;
  const activeAccessError = activeAccessRow
    ? connectionAccessErrors[activeAccessRow.id]
    : undefined;

  const loadConnectionAccessGrants = useCallback(async (
    row: ConnectionAccessRow,
    forceRefresh = false,
    options: LoadConnectionAccessGrantsOptions = {}
  ) => {
    if (!currentWorkspace?.id) {
      return;
    }
    if (!forceRefresh && connectionAccessById[row.id] && connectionManageAccessById[row.id]) {
      return;
    }
    if (loadingAccessConnectionIds.has(row.id)) {
      return;
    }

    const integrationId = getConnectionAccessBackendId(row);
    if (!integrationId) {
      setConnectionAccessErrors((previous) => ({
        ...previous,
        [row.id]: t("website.integrationsSettings.connectionAccessUnavailable"),
      }));
      return;
    }

    setLoadingAccessConnectionIds((previous) => new Set(previous).add(row.id));
    setConnectionAccessErrors((previous) => {
      const next = { ...previous };
      delete next[row.id];
      return next;
    });

    try {
      const response = await authService.getWorkspaceIntegrationAccessGrants(integrationId, {
        headers: {
          "X-Workspace-Id": currentWorkspace.id,
        },
      });
      const accessGrants = response.accessGrants ?? [];
      setConnectionAccessById((previous) => ({
        ...previous,
        [row.id]: buildAccessMatrixFromBackendGrants(accessGrants, members),
      }));
      setConnectionManageAccessById((previous) => ({
        ...previous,
        [row.id]: buildManageAccessMatrixFromBackendGrants(accessGrants, members),
      }));
    } catch (error: any) {
      const message = normalizeConnectionUserMessage(
        error?.message,
        t("website.integrationsSettings.failedToLoadConnectionAccess")
      );
      setConnectionAccessErrors((previous) => ({
        ...previous,
        [row.id]: message,
      }));
      if (options.silent) {
        setConnectionManageAccessById((previous) => ({
          ...previous,
          [row.id]: buildManageAccessMatrixFromBackendGrants([], members),
        }));
      } else {
        toast.showToast(message, "error");
      }
    } finally {
      setLoadingAccessConnectionIds((previous) => {
        const next = new Set(previous);
        next.delete(row.id);
        return next;
      });
    }
  }, [
    connectionAccessById,
    connectionManageAccessById,
    currentWorkspace?.id,
    loadingAccessConnectionIds,
    members,
    t,
    toast,
  ]);

  useEffect(() => {
    if (!currentWorkspace?.id || isConnectionAccessLoading) {
      return;
    }

    scopedVisibleRows.forEach((row) => {
      if (!shouldPrefetchConnectionAccess(row)) {
        return;
      }
      if (connectionManageAccessById[row.id]) {
        return;
      }

      void loadConnectionAccessGrants(row, false, { silent: true });
    });
  }, [
    connectionManageAccessById,
    currentWorkspace?.id,
    isConnectionAccessLoading,
    loadConnectionAccessGrants,
    scopedVisibleRows,
  ]);

  const connectionAccessViewState = useMemo<ConnectionAccessViewState>(() => {
    if (isConnectionAccessLoading && !hasConnectionRows) {
      return "loading";
    }

    return hasConnectionRows ? "ready" : "empty";
  }, [hasConnectionRows, isConnectionAccessLoading]);

  useEffect(() => {
    if (!activeAccessConnectionId) {
      return;
    }

    if (!activeAccessRow) {
      setActiveAccessConnectionId(null);
    }
  }, [activeAccessConnectionId, activeAccessRow]);

  useEffect(() => {
    if (!activeAccessRow) {
      setModalAccessDraft({});
      setModalManageAccessDraft({});
      setMembersPage(1);
      return;
    }

    setMembersPage(1);
    void loadConnectionAccessGrants(activeAccessRow);
    const savedAccess = connectionAccessById[activeAccessRow.id] ?? {};
    const savedManageAccess = connectionManageAccessById[activeAccessRow.id] ?? {};
    setModalAccessDraft(
      activeAssignableMembers.reduce<Record<string, ConnectionAccessLevel>>((next, member) => {
        next[member.key] = savedAccess[member.key] ?? "no_access";
        return next;
      }, {})
    );
    setModalManageAccessDraft(
      activeAssignableMembers.reduce<Record<string, boolean>>((next, member) => {
        next[member.key] = savedManageAccess[member.key] ?? (member.isCurrentUser && activeAccessRow.canManageAccess);
        return next;
      }, {})
    );
  }, [
    activeAccessRow,
    activeAssignableMemberKeys,
    activeAssignableMembers,
    connectionAccessById,
    connectionManageAccessById,
    loadConnectionAccessGrants,
  ]);

  const accessLabel = useCallback(
    (level: ConnectionAccessLevel) => {
      if (level === "action") return t("website.integrationsSettings.viewAndTakeAction");
      if (level === "read") return t("website.integrationsSettings.viewOnly");
      return t("website.integrationsSettings.noAccess");
    },
    [t]
  );

  const openUserAccessModal = useCallback((row: ConnectionAccessRow) => {
    if (!row.isManagedByCurrentUser || !isIntegrationConnectionActive(row)) {
      return;
    }

    setActiveAccessConnectionId(row.id);
  }, []);

  useEffect(() => {
    if (!requestedAccessProvider || isConnectionAccessLoading) {
      return;
    }

    const targetRow = findRequestedAccessRow(scopedVisibleRows, requestedAccessProvider);
    if (targetRow) {
      openUserAccessModal(targetRow);
    }

    onAccessRequestComplete?.();
  }, [
    isConnectionAccessLoading,
    onAccessRequestComplete,
    openUserAccessModal,
    requestedAccessProvider,
    scopedVisibleRows,
  ]);

  useEffect(() => {
    if (!highlightedConnectionKey && !highlightedConnectionProvider) {
      lastHighlightRequestRef.current = null;
      return;
    }

    const highlightedKey = normalizeConnectionIdentity(highlightedConnectionKey);
    const providerId = normalizeProvider(highlightedConnectionProvider);
    const highlightRequestId = highlightedKey ? `row:${highlightedKey}` : providerId ? `provider:${providerId}` : "";
    if (!highlightRequestId) {
      lastHighlightRequestRef.current = null;
      return;
    }

    if (lastHighlightRequestRef.current === highlightRequestId) {
      return;
    }

    if (isConnectionAccessLoading) {
      return;
    }

    const targetRow = highlightedKey
      ? scopedVisibleRows.find((row) => getConnectionRowIdentityKeys(row).has(highlightedKey))
      : [...scopedVisibleRows]
          .filter((row) => getConnectionGroupKey(row.provider) === providerId)
          .sort((left, right) => getConnectionOpenTimestamp(right) - getConnectionOpenTimestamp(left))[0];
    if (!targetRow) {
      return;
    }

    const rowElementKey = getConnectionRowElementKey(targetRow);
    const getTargetElement = () => (
      Array.from(document.querySelectorAll<HTMLElement>("[data-connection-row-key]"))
        .find((candidate) => candidate.dataset.connectionRowKey === rowElementKey)
      ?? document.getElementById(getConnectionGroupElementId(targetRow.provider))
    );
    const element = getTargetElement();

    if (element) {
      lastHighlightRequestRef.current = highlightRequestId;
      setHighlightedRowId(targetRow.id);

      let scrollFrameId = 0;
      let nestedScrollFrameId = 0;

      scrollFrameId = window.requestAnimationFrame(() => {
        nestedScrollFrameId = window.requestAnimationFrame(() => {
          const targetElement = getTargetElement();
          if (!(targetElement instanceof HTMLElement)) {
            return;
          }

          scrollConnectionGroupIntoView(targetElement);
          targetElement.focus({ preventScroll: true });
        });
      });

      const highlightTimeout = setTimeout(() => {
        setHighlightedRowId(null);
        onHighlightComplete?.();
      }, 5000);

      return () => {
        window.cancelAnimationFrame(scrollFrameId);
        window.cancelAnimationFrame(nestedScrollFrameId);
        clearTimeout(highlightTimeout);
      };
    }
  }, [
    highlightedConnectionKey,
    highlightedConnectionProvider,
    isConnectionAccessLoading,
    onHighlightComplete,
    scopedVisibleRows,
  ]);

  const closeUserAccessModal = useCallback(() => {
    setActiveAccessConnectionId(null);
    setMemberSearchQuery("");
    setModalManageAccessDraft({});
  }, []);

  const openEditConnectionLabelModal = useCallback((row: ConnectionAccessRow) => {
    if (!row.isManagedByCurrentUser) {
      return;
    }

    setEditingLabelConnection(row);
    setEditingLabelValue(row.alias || "");
  }, []);

  const closeEditConnectionLabelModal = useCallback(() => {
    if (isSavingConnectionLabel) {
      return;
    }

    setEditingLabelConnection(null);
    setEditingLabelValue("");
  }, [isSavingConnectionLabel]);

  const handleSaveConnectionLabel = useCallback(async () => {
    if (!editingLabelConnection || isSavingConnectionLabel) {
      return;
    }

    const nextLabel = editingLabelValue.trim();
    const integrationId = Number(editingLabelConnection.id);

    setIsSavingConnectionLabel(true);
    try {
      if (Number.isFinite(integrationId) && currentWorkspace?.id) {
        await authService.updateWorkspaceIntegrationLabel(integrationId, {
          label: nextLabel || null,
          axiosRequestConfig: {
            headers: {
              "X-Workspace-Id": currentWorkspace.id,
            },
          },
        });
      }

      setConnectionAliasOverrides((previous) => ({
        ...previous,
        [editingLabelConnection.id]: nextLabel,
      }));
      setEditingLabelConnection(null);
      setEditingLabelValue("");
      void integrations.refresh(false, { forceRefresh: true }).catch((error) => {
        console.warn("[ConnectionAccessSettings] Failed to refresh integrations after label update", error);
      });
    } catch (error: any) {
      toast.showToast(
        `${t("website.integrationsSettings.labelUpdateFailed")}: ${error.message || t("website.integrationsSettings.failedToUpdateIntegrationLabel")}`,
        "error"
      );
    } finally {
      setIsSavingConnectionLabel(false);
    }
  }, [
    currentWorkspace?.id,
    editingLabelConnection,
    editingLabelValue,
    integrations,
    isSavingConnectionLabel,
    t,
    toast,
  ]);

  const handleConnectionDisconnect = useCallback(async (row: ConnectionAccessRow) => {
    if (!row.isManagedByCurrentUser) {
      toast.showToast(t("website.integrationsSettings.disconnectPermissionDenied"), "error");
      return;
    }

    const connectionId = getConnectionDisconnectTrackingId(row);
    const disconnectOperationKey = `${row.provider}:${connectionId || row.id}`;
    if (disconnectOperationKeysRef.current.has(disconnectOperationKey)) {
      return;
    }

    disconnectOperationKeysRef.current.add(disconnectOperationKey);
    const connectionName = row.accountId || row.name || row.provider;
    const confirmed = await showDialog({
      title: t("website.integrationsSettings.disconnectTitle", { target: connectionName }),
      subtitle: t("website.integrationsSettings.disconnectSubtitle"),
      confirmText: t("website.integrationsSettings.disconnect"),
      cancelText: t("common.cancel"),
      destructive: true,
    });

    if (!confirmed) {
      disconnectOperationKeysRef.current.delete(disconnectOperationKey);
      return;
    }

    if (connectionId) {
      setDisconnectingConnectionIds((previous) => new Set(previous).add(connectionId));
    }

    try {
      if (isCustomMcpProvider(row.provider)) {
        const workspaceId = currentWorkspace?.id;
        if (!workspaceId) {
          throw new Error("Workspace ID is required");
        }

        const rowIntegrationId = String(row.workspaceIntegrationId || row.id);
        const rowConnectionId = String(row.connectionId || row.id);
        const connectors = await mcpConnectorService.list(workspaceId);
        const connector = connectors.find((item) => (
          String(item.integrationId ?? "") === rowIntegrationId ||
          String(item.connectorId) === rowConnectionId
        ));
        if (!connector) {
          throw new Error("MCP connector not found");
        }

        await mcpConnectorService.disconnect(workspaceId, connector.connectorId);
      } else {
        await integrations.disconnect(getDisconnectPlatformValue(row.provider), {
          connectionId,
          workspaceIntegrationId: row.workspaceIntegrationId || row.id,
          shopDomain: row.provider === "Shopify" ? row.accountId || row.name : undefined,
          authType: row.authType,
          native: row.native,
        });
      }

      if (activeAccessConnectionId === row.id) {
        setActiveAccessConnectionId(null);
      }
      if (editingLabelConnection?.id === row.id) {
        setEditingLabelConnection(null);
        setEditingLabelValue("");
      }
      if (viewingDetailsConnection?.id === row.id) {
        setViewingDetailsConnection(null);
      }

      await onConnectionDisconnected?.();
      toast.showToast(t("website.integrationsSettings.disconnectedSuccess", { tool: row.provider }), "success");
      void integrations.refresh(false, { forceRefresh: true }).catch((error) => {
        console.warn("[ConnectionAccessSettings] Failed to refresh integrations after disconnect", error);
      });
    } catch (error: any) {
      toast.showToast(
        `${t("website.integrationsSettings.disconnectFailed")}: ${normalizeConnectionUserMessage(error.message, t("website.integrationsSettings.failedToDisconnectFromTool", { tool: row.provider }))}`,
        "error"
      );
    } finally {
      disconnectOperationKeysRef.current.delete(disconnectOperationKey);
      if (connectionId) {
        setDisconnectingConnectionIds((previous) => {
          const next = new Set(previous);
          next.delete(connectionId);
          return next;
        });
      }
    }
  }, [
    activeAccessConnectionId,
    currentWorkspace?.id,
    editingLabelConnection?.id,
    integrations,
    onConnectionDisconnected,
    showDialog,
    t,
    toast,
    viewingDetailsConnection?.id,
  ]);

  const handleAccessDraftChange = (
    memberKey: string,
    level: ConnectionAccessLevel
  ) => {
    if (!activeAccessRow?.isManagedByCurrentUser) {
      return;
    }

    setModalAccessDraft((previous) => ({
      ...previous,
      [memberKey]: level,
    }));
  };

  const handleManageAccessDraftChange = (
    memberKey: string,
    checked: boolean
  ) => {
    if (!activeAccessRow?.isManagedByCurrentUser || !activeAccessRow.isConnectionOwnedByCurrentUser) {
      return;
    }

    setModalManageAccessDraft((previous) => ({
      ...previous,
      [memberKey]: checked,
    }));
  };

  const handleSaveUserAccess = useCallback(async () => {
    if (!activeAccessRow || !currentWorkspace?.id || isSavingActiveAccess || !activeAccessRow.isManagedByCurrentUser) {
      return;
    }

    const integrationId = getConnectionAccessBackendId(activeAccessRow);
    if (!integrationId) {
      toast.showToast(t("website.integrationsSettings.connectionAccessUnavailable"), "error");
      return;
    }

    const savedAccess = connectionAccessById[activeAccessRow.id] ?? {};
    const savedManageAccess = connectionManageAccessById[activeAccessRow.id] ?? {};
    const hasAccessManager = activeAssignableMembers.some((member) => (
      (modalManageAccessDraft[member.key] ?? savedManageAccess[member.key] ?? false)
    ));

    if (!hasAccessManager) {
      toast.showToast(t("website.integrationsSettings.connectionManageAccessRequired"), "error");
      return;
    }

    const grants = buildConnectionAccessUpdateGrants(
      activeAssignableMembers,
      modalAccessDraft,
      savedAccess,
      modalManageAccessDraft,
      savedManageAccess
    );
    if (grants.length === 0) {
      closeUserAccessModal();
      return;
    }

    setSavingAccessConnectionIds((previous) => new Set(previous).add(activeAccessRow.id));
    try {
      const response = await authService.updateWorkspaceIntegrationAccessGrants(integrationId, {
        grants,
        axiosRequestConfig: {
          headers: {
            "X-Workspace-Id": currentWorkspace.id,
          },
        },
      });
      const nextAccess = buildAccessMatrixFromBackendGrants(response.accessGrants ?? [], members);
      const nextManageAccess = buildManageAccessMatrixFromBackendGrants(
        response.accessGrants ?? [],
        members
      );
      setConnectionAccessById((previous) => ({
        ...previous,
        [activeAccessRow.id]: nextAccess,
      }));
      setConnectionManageAccessById((previous) => ({
        ...previous,
        [activeAccessRow.id]: nextManageAccess,
      }));
      toast.showToast(t("website.integrationsSettings.connectionAccessUpdated"), "success");
      void integrations.refresh(false, { forceRefresh: true }).catch((error) => {
        console.warn("[ConnectionAccessSettings] Failed to refresh integrations after access update", error);
      });
      closeUserAccessModal();
    } catch (error: any) {
      const message = normalizeConnectionUserMessage(
        error?.message,
        t("website.integrationsSettings.failedToSaveConnectionAccess")
      );
      toast.showToast(message, "error");
    } finally {
      setSavingAccessConnectionIds((previous) => {
        const next = new Set(previous);
        next.delete(activeAccessRow.id);
        return next;
      });
    }
  }, [
    activeAccessRow,
    activeAssignableMembers,
    closeUserAccessModal,
    connectionAccessById,
    connectionManageAccessById,
    currentWorkspace?.id,
    integrations,
    isSavingActiveAccess,
    members,
    modalAccessDraft,
    modalManageAccessDraft,
    t,
    toast,
  ]);

  const handleSelectAllAccess = useCallback((level: ConnectionAccessLevel) => {
    if (!activeAccessRow?.isManagedByCurrentUser) return;

    setModalAccessDraft((prev) => {
      const next = { ...prev };
      activeAssignableMembers.forEach((member) => {
        next[member.key] = level;
      });
      return next;
    });
  }, [activeAccessRow, activeAssignableMembers]);

  const selectAllState = useMemo(() => {
    if (!activeAccessRow) return { no_access: false, read: false, action: false };

    let noAccessCount = 0;
    let viewOnlyCount = 0;
    let actionCount = 0;
    const assignableCount = activeAssignableMembers.length;

    activeAssignableMembers.forEach((member) => {
      const savedAccess = connectionAccessById[activeAccessRow.id]?.[member.key] ?? "no_access";
      const access = modalAccessDraft[member.key] ?? savedAccess;
      if (access === "no_access") noAccessCount++;
      else if (access === "read") viewOnlyCount++;
      else if (access === "action") actionCount++;
    });

    return {
      no_access: assignableCount > 0 && noAccessCount === assignableCount,
      read: assignableCount > 0 && viewOnlyCount === assignableCount,
      action: assignableCount > 0 && actionCount === assignableCount,
    };
  }, [activeAccessRow, activeAssignableMembers, modalAccessDraft, connectionAccessById]);

  const [memberSearchQuery, setMemberSearchQuery] = useState("");

  const filteredAssignableMembers = useMemo(() => {
    const query = memberSearchQuery.trim().toLowerCase();
    if (!query) return activeAssignableMembers;
    return activeAssignableMembers.filter((member) =>
      member.name.toLowerCase().includes(query) ||
      member.email.toLowerCase().includes(query) ||
      member.role.toLowerCase().includes(query)
    );
  }, [activeAssignableMembers, memberSearchQuery]);

  const totalMembers = filteredAssignableMembers.length;
  const totalMembersPages = Math.max(1, Math.ceil(totalMembers / MEMBERS_PER_PAGE));
  const visibleMembersPages = useMemo(() => {
    if (totalMembersPages <= MAX_VISIBLE_PAGE_BUTTONS) {
      return Array.from({ length: totalMembersPages }, (_, idx) => idx + 1);
    }
    const half = Math.floor(MAX_VISIBLE_PAGE_BUTTONS / 2);
    let start = Math.max(1, membersPage - half);
    let end = start + MAX_VISIBLE_PAGE_BUTTONS - 1;
    if (end > totalMembersPages) {
      end = totalMembersPages;
      start = end - MAX_VISIBLE_PAGE_BUTTONS + 1;
    }
    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
  }, [membersPage, totalMembersPages]);

  const paginatedMembers = useMemo(() => {
    const startIndex = (membersPage - 1) * MEMBERS_PER_PAGE;
    return filteredAssignableMembers.slice(startIndex, startIndex + MEMBERS_PER_PAGE);
  }, [filteredAssignableMembers, membersPage]);

  return (
    <div className="space-y-6">
      {showHeader ? (
        <div className="max-w-3xl space-y-2">
          <h1 className="text-[20px] font-medium tracking-[-0.01em] text-[#1A162F]">
            {t("website.integrationsSettings.myConnectionsTitle")}
          </h1>
          <p className="max-w-2xl text-sm leading-6 text-slate-600">
            {t("website.integrationsSettings.connectionAccessSubtitle")}
          </p>
        </div>
      ) : null}


      {connectionAccessViewState === "loading" ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="grid grid-cols-[32%_18%_20%_30%] border-b border-slate-100 bg-slate-50/70 px-5 py-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-3 w-20 animate-pulse rounded bg-slate-100" />
            ))}
          </div>
          <div className="divide-y divide-slate-100">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="grid grid-cols-[32%_18%_20%_30%] items-center px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 shrink-0 animate-pulse rounded-lg bg-slate-100" />
                  <div className="space-y-2">
                    <div className="h-3 w-40 animate-pulse rounded bg-slate-100" />
                    <div className="h-3 w-52 animate-pulse rounded bg-slate-50" />
                  </div>
                </div>
                <div className="h-3 w-16 animate-pulse rounded bg-slate-100" />
                <div className="h-6 w-24 animate-pulse rounded-full bg-slate-100" />
                <div className="h-7 w-40 animate-pulse rounded-md bg-slate-100" />
              </div>
            ))}
          </div>
        </div>
      ) : connectionAccessViewState === "empty" ? (
        <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white px-6 text-center">
          <CirclesFour className="h-10 w-10 text-slate-300" />
          <h3 className="mt-4 text-base font-semibold text-slate-950">
            {t("website.integrationsSettings.noConnectionAccessRows")}
          </h3>
          <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
            {t("website.integrationsSettings.noConnectionAccessRowsDescription")}
          </p>
        </div>
      ) : (
        <div>
          {visibleRows.length === 0 ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white px-6 text-center">
              <CirclesFour className="h-9 w-9 text-slate-300" />
              <h3 className="mt-4 text-base font-semibold text-slate-950">
                {t("website.integrationsSettings.noConnectionMatches")}
              </h3>
              <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
                {t("website.integrationsSettings.noConnectionMatchesDescription")}
              </p>
            </div>
          ) : (
            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="overflow-x-auto">
                <table className="min-w-[880px] w-full table-fixed border-collapse text-left text-xs">
                  <colgroup>
                    <col className={showActionColumn ? "w-[32%]" : "w-[39%]"} />
                    <col className={showActionColumn ? "w-[15%]" : "w-[21%]"} />
                    <col className={showActionColumn ? "w-[16%]" : "w-[20%]"} />
                    <col className={showActionColumn ? "w-[17%]" : "w-[20%]"} />
                    {showActionColumn ? <col className="w-[20%]" /> : null}
                  </colgroup>
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/70">
                      <th className="px-5 py-4 text-xs font-medium text-slate-500">
                        {primaryColumnLabel}
                      </th>
                      <th className="px-4 py-4 text-xs font-medium text-slate-500">
                        {t("website.integrationsSettings.label")}
                      </th>
                      <th className="whitespace-nowrap px-4 py-4 text-xs font-medium text-slate-500">
                        {t("website.integrationsSettings.connectionStatus")}
                      </th>
                      <th className="whitespace-nowrap px-4 py-4 text-xs font-medium text-slate-500">
                        {t("website.integrationsSettings.myAccess")}
                      </th>
                      {showActionColumn ? (
                        <th className="whitespace-nowrap px-4 py-4 text-xs font-medium text-slate-500">
                          {t("website.integrationsSettings.action")}
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {scopedVisibleRows.map((row) => {
                      const providerKey = getConnectionGroupKey(row.provider);
                      const brandIcon = showProviderIcon ? getBrandIcon(row.provider) : "";
                      const isProviderAnchor = providerAnchorRowIds.get(providerKey) === row.id;
                      const rowElementKey = getConnectionRowElementKey(row);
                      const isHighlightedRow = highlightedRowId === row.id;
                      const connectionMetaLine = formatConnectionMetaLine(t, locale, row);
                      const isConnectionActive = isIntegrationConnectionActive(row);
                      const canOpenAccess = row.isManagedByCurrentUser && isConnectionActive;
                      const hasSubAccounts = row.subAccounts && row.subAccounts.length > 0;
                      const disconnectTrackingId = getConnectionDisconnectTrackingId(row);
                      const isConnectionDisconnecting = disconnectTrackingId
                        ? disconnectingConnectionIds.has(disconnectTrackingId)
                        : false;

                      return (
                        <tr
                          key={row.id}
                          id={isProviderAnchor ? getConnectionGroupElementId(row.provider) : undefined}
                          data-connection-row-key={rowElementKey}
                          tabIndex={isProviderAnchor ? -1 : undefined}
                          className={clsx(
                            "scroll-mt-6 outline-none transition-colors hover:bg-slate-50/40 focus:outline-none",
                            isHighlightedRow && "connection-row-highlight"
                          )}
                        >
                          <td className="px-5 py-4 align-middle">
                            <div className="flex min-w-0 items-center gap-3">
                              {showProviderIcon ? (
                                <div
                                  className={clsx(
                                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                                    getBrandIconFrameClassName(row.provider)
                                  )}
                                >
                                  <img
                                    src={brandIcon}
                                    alt=""
                                    title={row.provider}
                                    className={getBrandIconImageClassName(row.provider, "h-5 w-5")}
                                    onError={(event) => {
                                      event.currentTarget.style.display = "none";
                                    }}
                                  />
                                </div>
                              ) : null}
                              <div className="flex min-w-0 flex-col items-start">
                                <TruncatedTooltipText
                                  text={row.accountId}
                                  className="block max-w-full truncate text-sm font-semibold text-slate-950"
                                />
                                <TruncatedTooltipText
                                  text={connectionMetaLine}
                                  className="mt-1 block max-w-full truncate text-[11px] text-[#8A93B2]"
                                />
                                {hasSubAccounts ? (
                                  <button
                                    type="button"
                                    onClick={() => setViewingDetailsConnection(row)}
                                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#2F7BFF] transition-colors hover:text-[#1A5FE8]"
                                  >
                                    <span>{t("website.integrationsSettings.viewConnectionDetails")}</span>
                                    <CaretRight size={12} weight="bold" />
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 align-middle text-sm text-slate-600">
                            <div className="flex min-w-0 items-center gap-2 group/label">
                              <TruncatedTooltipText
                                text={row.alias || "-"}
                                className="block min-w-0 truncate"
                              />
                              {row.isManagedByCurrentUser ? (
                                <Tooltip content={t("website.integrationsSettings.editLabelAction")} side="top">
                                  <button
                                    type="button"
                                    onClick={() => openEditConnectionLabelModal(row)}
                                    aria-label={t("website.integrationsSettings.editLabelAction")}
                                    disabled={isConnectionDisconnecting}
                                    className="rounded-md p-1 text-slate-500 transition-all hover:bg-[#F4F5F8] hover:text-[#1A162F] focus-visible:bg-[#F4F5F8] focus-visible:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    <PencilSimple size={16} />
                                  </button>
                                </Tooltip>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-4 py-4 align-middle">
                            <ConnectionStatusText
                              status={row.status}
                              statusLabel={row.statusLabel}
                              isHighlighted={isHighlightedRow}
                            />
                          </td>
                          <td className="px-4 py-4 align-middle">
                            <TruncatedTooltipText
                              text={accessLabel(row.currentUserAccess)}
                              className="block max-w-full truncate text-xs font-medium text-[#1A162F]"
                            />
                          </td>
                          {showActionColumn ? (
                            <td className="px-4 py-4 align-middle">
                              {isConnectionActive && row.isManagedByCurrentUser ? (
                                <div className="-ml-2.5 flex flex-col items-start gap-1">
                                  <button
                                    type="button"
                                    onClick={() => openUserAccessModal(row)}
                                    disabled={!canOpenAccess || isConnectionDisconnecting}
                                    aria-label={t("website.integrationsSettings.manageConnectionAccess")}
                                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium text-[#1A162F] transition-colors hover:bg-[#F4F5F8] focus-visible:bg-[#F4F5F8] disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
                                  >
                                    <Users size={14} weight="bold" />
                                    <span>{t("website.integrationsSettings.manageConnectionAccess")}</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleConnectionDisconnect(row)}
                                    disabled={isConnectionDisconnecting}
                                    aria-label={t("website.integrationsSettings.disconnect")}
                                    className={clsx(
                                      "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                                      isConnectionDisconnecting
                                        ? "cursor-wait text-slate-400"
                                        : "text-red-600 hover:bg-red-50 focus-visible:bg-red-50"
                                    )}
                                  >
                                    {isConnectionDisconnecting ? (
                                      <CircleNotch size={14} weight="bold" className="animate-spin" />
                                    ) : (
                                      <>
                                        <LinkBreak size={14} weight="bold" />
                                        <span>{t("website.integrationsSettings.disconnect")}</span>
                                      </>
                                    )}
                                  </button>
                                </div>
                              ) : null}
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}

      <Modal
        isOpen={!!editingLabelConnection}
        onClose={closeEditConnectionLabelModal}
        title={t("website.integrationsSettings.editLabel")}
        width="max-w-md"
        bodyClassName="px-6 pb-6 pt-0"
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-950">
              {t("website.integrationsSettings.label")}
            </label>
            <input
              value={editingLabelValue}
              onChange={(event) => setEditingLabelValue(event.target.value)}
              placeholder={t("website.integrationsSettings.labelPlaceholder")}
              disabled={isSavingConnectionLabel}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-950 transition-all placeholder:text-slate-400 focus:border-[#1A162F]/30 focus:outline-none focus:ring-2 focus:ring-[#1A162F]/10 disabled:cursor-not-allowed disabled:bg-slate-50"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={closeEditConnectionLabelModal}
              disabled={isSavingConnectionLabel}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSaveConnectionLabel}
              disabled={isSavingConnectionLabel}
              className="rounded-lg bg-[#1A162F] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1A162F]/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSavingConnectionLabel ? (
                <span className="inline-flex items-center gap-2">
                  <CircleNotch size={14} className="animate-spin" />
                  {t("website.integrationsSettings.saving")}
                </span>
              ) : (
                t("common.save")
              )}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!viewingDetailsConnection}
        onClose={() => setViewingDetailsConnection(null)}
        title={t("website.integrationsSettings.connectionDetailsTitle", {
          tool: viewingDetailsConnection?.integrationType || "",
        })}
        variant="reference"
        width="max-w-md"
        bodyClassName="px-6 pb-6 pt-2"
      >
        <div className="divide-y divide-slate-100">
          {viewingDetailsConnection?.subAccounts?.map((subAccount) => (
            <div
              key={`${subAccount.accountName}-${subAccount.customerId}`}
              className="flex items-start gap-3 py-4 first:pt-2 last:pb-2"
            >
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#3B82F6] shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
              <div className="space-y-1.5">
                <div className="flex items-baseline gap-1 text-sm font-normal text-slate-950">
                  <span className="text-xs font-medium text-slate-400">
                    {t("website.integrationsSettings.idLabel")}:
                  </span>
                  <span>{subAccount.customerId}</span>
                </div>
                <div className="flex items-baseline gap-1 text-sm font-normal text-slate-950">
                  <span className="text-xs font-medium text-slate-400">
                    {t("website.integrationsSettings.nameLabel")}:
                  </span>
                  <span>{subAccount.accountName}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      <Modal
        isOpen={!!activeAccessRow}
        onClose={closeUserAccessModal}
        title={t("website.integrationsSettings.userAccessForConnection")}
        width="max-w-[1080px]"
        bodyClassName="px-6 pb-6 pt-1"
      >
        {activeAccessRow ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 -mt-2">
              {t("website.integrationsSettings.connectionAccessDescription")}
            </p>
            {activeAccessError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {activeAccessError}
              </div>
            ) : null}
            {activeAssignableMembers.length > 0 && (
              <div className="flex flex-col gap-3 px-1 text-xs sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full sm:max-w-[280px]">
                  <MagnifyingGlass size={16} weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={memberSearchQuery}
                    onChange={(e) => { setMemberSearchQuery(e.target.value); setMembersPage(1); }}
                    placeholder={t("website.integrationsSettings.searchMembers") || "Search users..."}
                    className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition-colors hover:border-slate-300 focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/10"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  <span className="font-medium text-[#666F8D]">
                    {t("website.integrationsSettings.applyToAll")}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleSelectAllAccess("no_access")}
                      disabled={!activeAccessRow.isManagedByCurrentUser || isActiveAccessLoading || isSavingActiveAccess}
                      className={clsx(
                        "rounded px-2.5 py-1 text-xs font-medium border transition-colors",
                        selectAllState.no_access
                          ? "bg-[#1A162F] border-[#1A162F] text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                      )}
                    >
                      {t("website.integrationsSettings.noAccess")}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSelectAllAccess("read")}
                      disabled={!activeAccessRow.isManagedByCurrentUser || isActiveAccessLoading || isSavingActiveAccess}
                      className={clsx(
                        "rounded px-2.5 py-1 text-xs font-medium border transition-colors",
                        selectAllState.read
                          ? "bg-[#1A162F] border-[#1A162F] text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                      )}
                    >
                      {t("website.integrationsSettings.viewOnly")}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSelectAllAccess("action")}
                      disabled={!activeAccessRow.isManagedByCurrentUser || isActiveAccessLoading || isSavingActiveAccess}
                      className={clsx(
                        "rounded px-2.5 py-1 text-xs font-medium border transition-colors",
                        selectAllState.action
                          ? "bg-[#1A162F] border-[#1A162F] text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                      )}
                    >
                      {t("website.integrationsSettings.viewAndTakeAction")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {isActiveAccessLoading ? (
              <ConnectionAccessTableSkeleton />
            ) : activeAssignableMembers.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-4 text-sm text-slate-500">
                {t("website.integrationsSettings.noAssignableMembers")}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  {/* Column Headers */}
                  <div className="grid grid-cols-[minmax(300px,1fr)_140px_200px_156px] gap-4 items-center border-b border-slate-100 bg-slate-50/40 px-4 py-2.5">
                    <div className="min-w-0 pl-11">
                      <span className="text-xs font-medium text-[#666F8D]">
                        {t("website.integrationsSettings.user")}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs font-medium text-[#666F8D]">
                        {t("website.integrationsSettings.role")}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs font-medium text-[#666F8D]">
                        {t("website.integrationsSettings.connectionAccessLevel")}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
                        <span className="text-xs font-medium text-[#666F8D] whitespace-nowrap">
                          {t("website.integrationsSettings.canManageAccess")}
                        </span>
                        <Tooltip
                          content={t("website.integrationsSettings.canManageAccessHelp")}
                          side="top"
                          align="center"
                          sideOffset={6}
                          contentClassName="!w-[420px] !max-w-[calc(100vw-32px)] !whitespace-normal !text-left"
                        >
                          <button
                            type="button"
                            aria-label={t("website.integrationsSettings.canManageAccessHelp")}
                            className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-[#8A93B2] transition-colors hover:text-[#1A162F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2F7BFF]/20"
                          >
                            <Question size={14} weight="bold" />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>

                  {/* Compact User List */}
                  <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto custom-scrollbar sm:max-h-[55vh]">
                    {paginatedMembers.length > 0 ? (
                      paginatedMembers.map((member) => {
                        const isCreator = member.key === activeAccessRow.connectionOwnerKey;
                        const canEditManageAccess = activeAccessRow.isConnectionOwnedByCurrentUser;
                        const isCurrentUserGrantedManager = member.isCurrentUser && activeAccessRow.canManageAccess;
                        const savedAccess = connectionAccessById[activeAccessRow.id]?.[member.key] ?? "no_access";
                        const savedManageAccess = connectionManageAccessById[activeAccessRow.id]?.[member.key] ?? false;
                        const access = modalAccessDraft[member.key] ?? savedAccess;
                        const canManageAccess = isCurrentUserGrantedManager
                          ? true
                          : modalManageAccessDraft[member.key] ?? savedManageAccess;
                        const locked = !activeAccessRow.isManagedByCurrentUser || isActiveAccessLoading || isSavingActiveAccess;
                        const displayRole = getMemberDisplayRole(member, t);

                        return (
                          <div
                            key={`${activeAccessRow.id}-${member.key}`}
                            className="grid grid-cols-[minmax(300px,1fr)_140px_200px_156px] gap-4 items-center px-4 py-3 hover:bg-slate-50/40 transition-colors"
                          >
                            {/* Identity */}
                            <div className="flex min-w-0 items-center gap-3">
                              {member.avatar ? (
                                <img
                                  src={member.avatar}
                                  alt=""
                                  className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-slate-100"
                                />
                              ) : (
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                                  {member.name.slice(0, 1).toUpperCase()}
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <TruncatedTooltipText
                                    text={member.name}
                                    className="block min-w-0 truncate text-sm font-medium text-slate-900"
                                  />
                                  {member.isCurrentUser && (
                                    <span className="shrink-0 rounded-full bg-[#1A162F]/5 px-1.5 py-0.5 text-[10px] font-semibold text-[#1A162F]">
                                      {t("website.integrationsSettings.you")}
                                    </span>
                                  )}
                                  {isCreator && (
                                    <span className="shrink-0 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600 ring-1 ring-inset ring-blue-500/20">
                                      {t("website.integrationsSettings.connectionOwner")}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 text-xs text-slate-500">
                                  <TruncatedTooltipText
                                    text={member.email || t("website.integrationsSettings.member")}
                                    className="block min-w-0 truncate"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Role */}
                            <div className="min-w-0">
                              <TruncatedTooltipText
                                text={displayRole || t("website.integrationsSettings.member")}
                                className="block min-w-0 truncate text-sm font-normal text-[#666F8D]"
                              />
                            </div>

                            {/* Access Level Dropdown */}
                            <div className="flex justify-start">
                              <AccessLevelDropdown
                                value={access}
                                onChange={(level) => handleAccessDraftChange(member.key, level)}
                                disabled={locked}
                                accessLabel={accessLabel}
                              />
                            </div>

                            {/* Manage Access Toggle */}
                            <div className="flex justify-start">
                              {canEditManageAccess ? (
                                <Tooltip
                                  content={isCreator ? t("website.integrationsSettings.connectionOwnerManageAccessLocked") : undefined}
                                  side="top"
                                  align="center"
                                  sideOffset={6}
                                >
                                  <span
                                    className={clsx("inline-flex", isCreator && "cursor-not-allowed [&>button]:pointer-events-none")}
                                    tabIndex={isCreator ? 0 : undefined}
                                    aria-label={isCreator ? t("website.integrationsSettings.connectionOwnerManageAccessLocked") : undefined}
                                  >
                                    <Switch
                                      checked={canManageAccess}
                                      disabled={isCreator || locked}
                                      onChange={(checked) => handleManageAccessDraftChange(member.key, checked)}
                                      ariaLabel={`${member.name} - ${t("website.integrationsSettings.manageOthersAccess")}`}
                                    />
                                  </span>
                                </Tooltip>
                              ) : (
                                <ManageAccessStatus
                                  enabled={canManageAccess}
                                  enabledLabel={t("website.integrationsSettings.canManageAccessStatus")}
                                  disabledLabel={t("website.integrationsSettings.cannotManageAccessStatus")}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-text-sub">
                        <User size={48} weight="duotone" className="mb-4 opacity-50" />
                        <p>
                          {t("website.membersSettings.noUsersMatching", { query: memberSearchQuery })}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Pagination Footer */}
                  {totalMembers > MEMBERS_PER_PAGE ? (
                    <div className="flex items-center justify-between border-t border-[#ECEFF5] bg-[#FCFCFC] px-6 py-2.5 rounded-lg border">
                      <span className="text-xs font-normal text-[#666F8D]">
                        {t("website.billing.showingHistoryRange", {
                          start: (membersPage - 1) * MEMBERS_PER_PAGE + 1,
                          end: Math.min(membersPage * MEMBERS_PER_PAGE, totalMembers),
                          total: totalMembers,
                        })}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setMembersPage((page) => Math.max(1, page - 1))}
                          disabled={membersPage === 1}
                          className="rounded-lg p-1.5 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <CaretLeft size={14} weight="regular" />
                        </button>
                        <div className="flex items-center gap-1">
                          {visibleMembersPages.map((page) => (
                            <button
                              key={page}
                              type="button"
                              onClick={() => setMembersPage(page)}
                              className={clsx(
                                "h-7 w-7 rounded-md text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                                membersPage === page
                                  ? "bg-[#1A162F] text-white hover:bg-primary/90"
                                  : "text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]"
                              )}
                            >
                              {page}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setMembersPage((page) => Math.min(totalMembersPages, page + 1))}
                          disabled={membersPage === totalMembersPages}
                          className="rounded-lg p-1.5 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <CaretRight size={14} weight="regular" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeUserAccessModal}
                className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleSaveUserAccess}
                disabled={!activeAccessRow.isManagedByCurrentUser || isActiveAccessLoading || isSavingActiveAccess}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("website.integrationsSettings.updateAccess")}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default ConnectionAccessSettings;
